import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Check, Star, Gift } from 'lucide-react';

export const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly, semiannual, annual

  const calculateDiscountedPrice = (basePrice, cycle) => {
    switch (cycle) {
      case 'semiannual':
        return Math.round(basePrice * 6 * 0.9); // 10% discount
      case 'annual':
        return Math.round(basePrice * 12 * 0.8); // 20% discount
      default:
        return basePrice;
    }
  };

  const getPriceText = (price, cycle) => {
    const formattedPrice = setPoint(price);
    switch (cycle) {
      case 'semiannual':
        return (
          <div className="flex flex-col items-center">
            <span className="text-3xl lg:text-4xl font-bold">${formattedPrice}</span>
            <span className="text-xs font-bold text-gray-500 mt-1">cada 6 meses</span>
          </div>
        );
      case 'annual':
        return (
          <div className="flex flex-col items-center">
            <span className="text-3xl lg:text-4xl font-bold">${formattedPrice}</span>
            <span className="text-xs font-bold text-gray-500 mt-1">anual</span>
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center">
            <span className="text-3xl lg:text-4xl font-bold">${formattedPrice}</span>
            <span className="text-xs font-bold text-gray-500 mt-1">/ mes</span>
          </div>
        );
    }
  };

  const setPoint = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const getMonthlyEquivalent = (price, cycle) => {
    switch (cycle) {
      case 'semiannual':
        return Math.round(price / 6);
      case 'annual':
        return Math.round(price / 12);
      default:
        return price;
    }
  };

  const plans = [
    {
      name: "Gratis",
      price: 0,
      description: "¿Dudas? Pruébalo tu mismo",
      features: [
        "Configuración de tienda online",
        "Hasta 30 productos",
        "Soporte por email",
        "Carrito de Compra",
        "Botón de WhatsApp",
        "Integración Redes Sociales",
        "Certificado SSL",
        "Diseño responsive para móviles"
      ],
      buttonText: "Comenzar Gratis",
      popular: false,
      freeTrial: true
    },
    {
      name: "Básico",
      basePrice: 3999,
      description: "Perfecto para empezar",
      features: [
        "Todo lo del plan Gratis",
        "Hasta 100 productos",
        "Soporte en carga de productos",
        "Ajustes adicionales",
        "Reseñas de Google",
        "Panel de análisis",
        "Lanzamiento de Promociones",
        "Estádisticas de productos mas visitados",
      ],
      buttonText: "Elegir Básico",
      popular: false
    },
    {
      name: "Profesional",
      basePrice: 14999,
      description: "Para negocios en crecimiento",
      features: [
        "Todo lo del plan Básico",
        "Sistema CRM avanzado",
        "Todas las 50+ plantillas de listados",
        "Hasta 500 productos",
        "Soporte prioritario",
        "Panel de análisis avanzado",
        "Marca personalizada",
        "Dominio personalizado (.com.ar)",
        //"Acceso a API"
      ],
      buttonText: "Elegir Profesional",
      popular: true
    },
    {
      name: "Empresa",
      basePrice: 45999,
      description: "Para operaciones a gran escala",
      features: [
        "Todo lo del plan Profesional",
        "Desarrollo personalizado",
        "Productos ilimitados",
        "Integración con IA",
        "Integraciones avanzadas",
        "Gestión multi-tienda",
        "Soporte telefónico 24/7",
        "Capacitación personalizada",
        "Acceso prioritaro a nuevas funcionalidades"
      ],
      buttonText: "Elegir Empresa",
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-12 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-8 lg:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540]">
            Planes y Precios
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Elige el plan que se ajuste a tu negocio. Ahorra hasta 20% con planes anuales.
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8 lg:mb-12">
          <div className="bg-gray-100 rounded-lg p-1 flex flex-wrap justify-center gap-1 sm:gap-0">
            {[
              { id: 'monthly', label: 'Mensual' },
              { id: 'semiannual', label: 'Semestral (-10%)' },
              { id: 'annual', label: 'Anual (-20%)' }
            ].map((option) => (
              <button
                key={option.id}
                onClick={() => setBillingCycle(option.id)}
                className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${billingCycle === option.id
                    ? 'bg-[#5750f1] text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => {
            const price = plan.freeTrial
              ? 0
              : calculateDiscountedPrice(plan.basePrice, billingCycle);
            const monthlyEquivalent = plan.freeTrial
              ? 0
              : getMonthlyEquivalent(price, billingCycle);

            return (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-1 ${plan.popular
                    ? 'border-[#5750f1] shadow-xl lg:shadow-2xl lg:scale-105'
                    : plan.freeTrial
                      ? 'border-green-300 shadow-xl'
                      : 'border-gray-200 hover:shadow-lg'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-[#5750f1] to-[#3B30A4] text-white text-center py-2 text-xs sm:text-sm font-semibold">
                    <Star className="inline w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Más Popular
                  </div>
                )}

                {plan.freeTrial && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-green-600 text-white text-center py-2 text-xs sm:text-sm font-semibold">
                    <Gift className="inline w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    {/* 3 Meses Gratis */}
                    Gratis
                  </div>
                )}

                <CardHeader className={`text-center ${(plan.popular || plan.freeTrial) ? 'pt-10 sm:pt-12' : 'pt-6 sm:pt-8'} pb-4 sm:pb-6`}>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A2540] mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm">
                    {plan.description}
                  </p>
                  <div className="space-y-1">
                    <div className="text-[#0A2540]">
                      {plan.freeTrial ? (
                        <span className="text-3xl sm:text-4xl font-bold">$0</span>
                      ) : (
                        getPriceText(price, billingCycle)
                      )}
                    </div>
                    {!plan.freeTrial && monthlyEquivalent > 0 && (
                      <div className="text-xs text-gray-500">
                        Equivale a ${setPoint(monthlyEquivalent)}/mes
                      </div>
                    )}
                    {plan.freeTrial && (
                      <div className="text-xs sm:text-sm text-green-600 font-semibold">
                        {/* Luego ${setPoint(plans[1].basePrice)}/mes */}
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-2">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 text-xs sm:text-sm leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full py-2 sm:py-3 text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-105 ${plan.popular
                        ? 'bg-[#5750f1] hover:bg-[#0042CC] text-white shadow-lg'
                        : plan.freeTrial
                          ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                          : 'bg-gray-100 hover:bg-[#5750f1] hover:text-white text-[#0A2540] border border-gray-200'
                      }`}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-8 lg:mt-12 space-y-4">
          <p className="text-gray-600 text-sm sm:text-base">
            Sin tarifas de configuración • Cancela en cualquier momento
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
              <span>99.9% Uptime SLA</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
              <span>Monitoreo 24/7</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};